import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Chip,
  Grid,
  MenuItem,
  Skeleton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import {
  AccountBalanceWalletOutlined as ResteIcon,
  CalendarMonthOutlined as MoisIcon,
  NorthEast as EncaisseIcon,
  PaymentsOutlined as PayIcon,
  ReceiptLongOutlined as CommandeIcon,
} from '@mui/icons-material';
import { GlassSurface } from '../../../components/GlassDashboard';
import useAlert from '../../../hooks/useAlert';
import AlertDialog from '../../../components/AlertDialog';
import usePdfPreview from '../../../hooks/usePdfPreview';
import { usePermissions } from '../../../contexts/PermissionContext';
import { extractErrorMessage } from '../../../utils/errorHandler';
import { getModuleTableTheme } from '../../../theme/tableTheme';
import { comptoirPaiementsAPI } from '../../../services/api';
import { useComptoirFinance, useInvalidateComptoir } from '../../../hooks/queries/useComptoir';
import EncaisserDialog from '../components/EncaisserDialog';
import { COLOR, ETAT_TONE, PAIEMENT_TONE, Panel, StateChip, dh, fDate, fmtPhone, num, today } from '../ui';

const MONEY = { fontFamily: 'monospace', whiteSpace: 'nowrap' } as const;

/** Suivi financier: what every client still owes, the unpaid services and the journal (after the prototype). */
const ComptoirFinance: React.FC = () => {
  const navigate = useNavigate();
  const { hasPermission } = usePermissions();
  const canPay = hasPermission('Comptoir', 'add', 'Services & Projets');
  const canClients = hasPermission('Comptoir', 'view', 'Clients');
  const { data: d, isLoading, error } = useComptoirFinance();
  const invalidate = useInvalidateComptoir();
  const { openPreview, pdfPreviewElement } = usePdfPreview();
  const { alertState, showError, hideAlert } = useAlert();
  const [tri, setTri] = useState<'montant' | 'echeance'>('montant');
  const [payClient, setPayClient] = useState<{ id: number; nom: string | null } | null>(null);
  const t = today();

  const clients = useMemo(() => {
    const xs = (d?.clients || []).slice();
    return tri === 'echeance'
      ? xs.sort((a, b) => (a.echeance || '9').localeCompare(b.echeance || '9'))
      : xs;  // the API sends them by amount owed
  }, [d, tri]);

  const printBon = async (paiementId: number, reference: string) => {
    try {
      const p = await comptoirPaiementsAPI.get(paiementId);
      openPreview({
        getBlob: async () => (await import('../../../utils/comptoirBonsPdfReactPdf')).getBonPaiementPDFBlob(p),
        fileName: `Bon_${reference}.pdf`,
      });
    } catch (e: any) { showError(extractErrorMessage(e)); }
  };

  const mois = new Date().toLocaleDateString('fr-FR', { month: 'long', year: 'numeric' });
  const k = d?.kpis;
  const kpis = [
    { title: 'Total commandé', value: dh(k?.commande), sub: `${k?.services ?? 0} service(s) / projet(s)`, color: '#607D8B', icon: <CommandeIcon /> },
    { title: 'Total encaissé', value: dh(k?.encaisse), sub: 'tous paiements', color: '#2E7D32', icon: <EncaisseIcon /> },
    { title: 'Reste à encaisser', value: dh(k?.reste), sub: `${k?.clients_dus ?? 0} client(s) concerné(s)`, color: '#D32F2F', icon: <ResteIcon /> },
    { title: 'Encaissé ce mois', value: dh(k?.encaisse_mois), sub: `${k?.paiements_mois ?? 0} paiement(s) · ${mois}`, color: COLOR, icon: <MoisIcon /> },
  ];

  return (
    <Box>
      <Box sx={{ mb: 2 }}>
        <Typography variant="h4" sx={{ fontWeight: 700, color: COLOR }}>Suivi financier</Typography>
        <Typography variant="body2" color="text.secondary">
          Ce que chaque client doit encore, les services non soldés et le journal des paiements et des changements de prix.
        </Typography>
      </Box>

      {!!error && <Alert severity="error" sx={{ mb: 2 }}>{extractErrorMessage(error)}</Alert>}

      <Grid container spacing={2} sx={{ mb: 2.5 }}>
        {kpis.map((c) => (
          <Grid item xs={12} sm={6} lg={3} key={c.title}>
            <GlassSurface accentColor={c.color} sx={{ p: 2, height: '100%' }}>
              <Stack direction="row" justifyContent="space-between" alignItems="flex-start" spacing={1}>
                <Box sx={{ minWidth: 0 }}>
                  <Typography variant="body2" color="text.secondary">{c.title}</Typography>
                  {d ? <Typography variant="h5" sx={{ fontWeight: 700 }} noWrap>{c.value}</Typography> : <Skeleton width={120} height={36} />}
                  <Typography variant="caption" color="text.secondary">{c.sub}</Typography>
                </Box>
                <Box sx={{ color: c.color, '& svg': { fontSize: 30 } }}>{c.icon}</Box>
              </Stack>
            </GlassSurface>
          </Grid>
        ))}
      </Grid>

      {/* ---------- clients ---------- */}
      <Panel title="Clients avec un solde dû" icon={<ResteIcon />} count={clients.length}
        action={
          <TextField select size="small" value={tri} onChange={(e) => setTri(e.target.value as 'montant' | 'echeance')} sx={{ minWidth: 260, bgcolor: 'background.paper' }}>
            <MenuItem value="montant">Trier par montant dû</MenuItem>
            <MenuItem value="echeance">Trier par échéance la plus ancienne</MenuItem>
          </TextField>
        }>
        <TableContainer sx={getModuleTableTheme('comptoir')}>
          <Table size="small" sx={{ minWidth: 900 }}>
            <TableHead>
              <TableRow>
                <TableCell>Client</TableCell><TableCell>Téléphone</TableCell><TableCell align="right">Services non soldés</TableCell>
                <TableCell>Échéance la plus proche</TableCell><TableCell>Dernier paiement</TableCell><TableCell align="right">Solde dû</TableCell>
                <TableCell align="right" width={130} />
              </TableRow>
            </TableHead>
            <TableBody>
              {isLoading ? Array.from({ length: 3 }).map((_, i) => (
                <TableRow key={i}>{Array.from({ length: 7 }).map((__, j) => <TableCell key={j}><Skeleton /></TableCell>)}</TableRow>
              )) : clients.length === 0 ? (
                <TableRow><TableCell colSpan={7} align="center" sx={{ py: 3, color: 'text.secondary' }}>Aucun montant dû.</TableCell></TableRow>
              ) : clients.map((c) => (
                <TableRow key={c.client_id} hover onClick={canClients ? () => navigate(`/comptoir/clients/${c.client_id}`) : undefined}
                  sx={{ cursor: canClients ? 'pointer' : undefined }}>
                  <TableCell sx={{ fontWeight: 600 }}>
                    {c.client_nom || '—'}
                    {c.livres > 0 && <Chip label={`${c.livres} livré(s)`} size="small" color="error" variant="outlined" sx={{ ml: 1, height: 20 }} />}
                  </TableCell>
                  <TableCell sx={{ fontFamily: 'monospace' }}>{fmtPhone(c.telephone) || '—'}</TableCell>
                  <TableCell align="right">{c.services}</TableCell>
                  <TableCell sx={{ color: c.echeance && c.echeance < t ? 'error.main' : undefined, fontWeight: c.echeance && c.echeance < t ? 600 : undefined }}>
                    {fDate(c.echeance)}
                  </TableCell>
                  <TableCell>{fDate(c.dernier_paiement)}</TableCell>
                  <TableCell align="right" sx={{ ...MONEY, fontWeight: 700, color: 'error.main' }}>{dh(c.solde)}</TableCell>
                  <TableCell align="right">
                    {canPay && (
                      <Button size="small" variant="outlined" startIcon={<PayIcon />}
                        onClick={(e) => { e.stopPropagation(); setPayClient({ id: c.client_id, nom: c.client_nom }); }}>
                        Encaisser
                      </Button>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Panel>

      {/* ---------- services ---------- */}
      <Panel title="Services non soldés" icon={<CommandeIcon />} count={d?.services.length ?? ''}>
        <TableContainer sx={getModuleTableTheme('comptoir')}>
          <Table size="small" sx={{ minWidth: 1000 }}>
            <TableHead>
              <TableRow>
                <TableCell>Réf.</TableCell><TableCell>Client</TableCell><TableCell>Désignation</TableCell><TableCell>Deadline</TableCell>
                <TableCell align="right">Prix</TableCell><TableCell align="right">Payé</TableCell><TableCell align="right">Reste</TableCell>
                <TableCell>Paiement</TableCell><TableCell>État</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {!d ? null : d.services.length === 0 ? (
                <TableRow><TableCell colSpan={9} align="center" sx={{ py: 3, color: 'text.secondary' }}>Tous les services sont soldés.</TableCell></TableRow>
              ) : d.services.map((s) => (
                <TableRow key={s.id} hover onClick={() => navigate(`/comptoir/services/${s.id}`)} sx={{ cursor: 'pointer' }}>
                  <TableCell sx={{ fontFamily: 'monospace', fontWeight: 700, color: COLOR, whiteSpace: 'nowrap' }}>{s.reference}</TableCell>
                  <TableCell>{s.client_nom || '—'}</TableCell>
                  <TableCell sx={{ maxWidth: 320 }}>{s.titre}</TableCell>
                  <TableCell sx={{ whiteSpace: 'nowrap', color: s.deadline && s.deadline < t && !['livre', 'cloture'].includes(s.etat) ? 'error.main' : undefined }}>{fDate(s.deadline)}</TableCell>
                  <TableCell align="right" sx={MONEY}>{dh(s.total)}</TableCell>
                  <TableCell align="right" sx={{ ...MONEY, color: 'success.main' }}>{dh(s.paye)}</TableCell>
                  <TableCell align="right" sx={{ ...MONEY, fontWeight: 700, color: 'error.main' }}>{dh(s.reste)}</TableCell>
                  <TableCell><StateChip label={s.paiement_label} t={PAIEMENT_TONE[s.paiement]} small /></TableCell>
                  <TableCell><StateChip label={s.etat_label} t={ETAT_TONE[s.etat]} small /></TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Panel>

      {/* ---------- journal ---------- */}
      <Panel title="Journal financier" icon={<PayIcon />} count={d ? `${d.journal.length} opération(s)` : ''}>
        <TableContainer sx={getModuleTableTheme('comptoir')}>
          <Table size="small" sx={{ minWidth: 1000 }}>
            <TableHead>
              <TableRow>
                <TableCell>Date</TableCell><TableCell>Opération</TableCell><TableCell>Référence</TableCell><TableCell>Client</TableCell>
                <TableCell>Détail</TableCell><TableCell align="right">Montant</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {!d ? null : d.journal.length === 0 ? (
                <TableRow><TableCell colSpan={6} align="center" sx={{ py: 3, color: 'text.secondary' }}>Aucune opération financière.</TableCell></TableRow>
              ) : d.journal.map((e, i) => {
                const prix = e.type === 'prix';
                const m = num(e.montant);
                return (
                  <TableRow key={i} hover sx={{ cursor: 'pointer' }}
                    onClick={() => (prix && e.service_id ? navigate(`/comptoir/services/${e.service_id}`) : e.paiement_id && printBon(e.paiement_id, e.reference))}>
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>{fDate(e.date)}</TableCell>
                    <TableCell>{prix ? <Chip label={e.libelle} size="small" color="warning" variant="outlined" /> : e.libelle}</TableCell>
                    <TableCell sx={{ fontFamily: 'monospace', fontWeight: 700, color: COLOR }}>{e.reference}</TableCell>
                    <TableCell>{e.client_nom || '—'}</TableCell>
                    <TableCell sx={{ color: 'text.secondary', fontSize: 13 }}>{e.detail}</TableCell>
                    <TableCell align="right" sx={{ ...MONEY, fontWeight: 700, color: prix ? (m < 0 ? 'error.main' : 'text.primary') : 'success.main' }}>
                      {prix && m > 0 ? '+' : ''}{dh(e.montant)}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      </Panel>

      <EncaisserDialog
        open={!!payClient} clientId={payClient?.id ?? null} clientNom={payClient?.nom}
        onClose={() => setPayClient(null)}
        onSaved={(p) => {
          setPayClient(null);
          invalidate();
          openPreview({
            getBlob: async () => (await import('../../../utils/comptoirBonsPdfReactPdf')).getBonPaiementPDFBlob(p),
            fileName: `Bon_${p.reference}.pdf`,
          });
        }}
      />
      <AlertDialog open={alertState.open} type={alertState.type} title={alertState.title} message={alertState.message}
        onClose={hideAlert} onConfirm={alertState.onConfirm} confirmText={alertState.confirmText}
        cancelText={alertState.cancelText} showCancel={alertState.showCancel} />
      {pdfPreviewElement}
    </Box>
  );
};

export default ComptoirFinance;
